// Pure display and decision helpers for Device Details "Wifi Network" / "Mobile Network" and the
// shared Wi-Fi controls (documentation/protocol.md "Wi-Fi remote control", "Saved Wi-Fi networks",
// "Cellular signal"). Kept free of React so they are unit-tested directly.
import type { DeviceInfo, WifiRequestTicket, WifiSavedNetwork, WifiSavedOperation } from '../../services/admin';

// Telemetry arrives with every heartbeat (about every 10 s); older than this is shown as stale.
export const TELEMETRY_STALE_MS = 2 * 60_000;
// The server refuses a delete based on a snapshot older than 10 minutes.
export const SAVED_SNAPSHOT_MAX_AGE_MS = 10 * 60_000;
// ... and a new deletion while one younger than this awaits the radio's result.
export const SAVED_PENDING_WINDOW_MS = 5 * 60_000;

export const ageMs = (iso: string | null | undefined, now: number): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, now - t);
};

export const ageText = (iso: string | null | undefined, now: number): string | null => {
  const ms = ageMs(iso, now);
  if (ms === null) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
};

export const isStale = (iso: string | null | undefined, now: number, maxMs = TELEMETRY_STALE_MS): boolean => {
  const ms = ageMs(iso, now);
  return ms === null || ms > maxMs;
};

export const wifiBand = (frequencyMhz: number | null | undefined): string | null => {
  if (!frequencyMhz) return null;
  const band = frequencyMhz < 2500 ? '2.4 GHz' : frequencyMhz < 5925 ? '5 GHz' : '6 GHz';
  return `${band} · ${frequencyMhz} MHz`;
};

export const wifiStateText = (info: Pick<DeviceInfo, 'wifi_enabled' | 'wifi_ssid'>): string => {
  if (info.wifi_enabled === null || info.wifi_enabled === undefined) return 'Not reported';
  if (!info.wifi_enabled) return 'Disabled';
  return info.wifi_ssid ? 'Enabled · connected' : 'Enabled · not connected';
};

const CELLULAR_STATUS_TEXT: Record<string, string> = {
  NO_SIGNAL: 'No signal',
  NO_PERMISSION: 'Not available (the app may not read the signal)',
  NO_DATA_SUBSCRIPTION: 'Not available (no active mobile-data SIM)',
  UNSUPPORTED: 'Not available on this radio',
  ERROR: 'Not available (the radio could not read it)',
};

// The genuine cellular signal of the active data subscription. Never the legacy mixed
// signal_strength_dbm, which may be a Wi-Fi RSSI.
export const cellularSignalText = (info: Pick<DeviceInfo, 'cellular_signal_status' | 'cellular_signal_dbm' | 'cellular_signal_technology'>): string => {
  const status = info.cellular_signal_status;
  if (!status) return 'Not reported by this radio’s app version';
  if (status === 'OK' && typeof info.cellular_signal_dbm === 'number') {
    return `${info.cellular_signal_dbm} dBm${info.cellular_signal_technology ? ` (${info.cellular_signal_technology})` : ''}`;
  }
  return CELLULAR_STATUS_TEXT[status] ?? 'Not available';
};

export type WifiResultKind = 'scan' | 'operation';

// Whether the radio's result for this request has arrived. A result is matched by request id; a
// newer request by someone else supersedes ours.
export type WifiResultState = 'waiting' | 'done' | 'superseded';

export const wifiResultState = (kind: WifiResultKind, info: DeviceInfo, ticket: WifiRequestTicket): WifiResultState => {
  const currentId = kind === 'scan' ? info.wifi_scan_request_id : info.wifi_operation_request_id;
  if (currentId && currentId !== ticket.request_id) return 'superseded';
  if (kind === 'scan') return info.wifi_scan_status === 'COMPLETED' || info.wifi_scan_status === 'FAILED' ? 'done' : 'waiting';
  return info.wifi_operation_state && info.wifi_operation_state !== 'PENDING' ? 'done' : 'waiting';
};

export const operationSucceeded = (info: DeviceInfo): boolean =>
  info.wifi_operation_state === 'CONNECTED' || info.wifi_operation_state === 'IDLE';

// --- Saved networks -------------------------------------------------------------------------

const SAVED_REASON_TEXT: Record<string, string> = {
  NOT_SYSTEM_APP: 'the app does not run as a system app on this radio',
  PERMISSION_DENIED: 'the radio’s firmware refused access',
  LIST_RESTRICTED: 'the radio’s firmware hides its saved networks from the app',
  WIFI_DISABLED: 'Wi-Fi is disabled',
  WIFI_SERVICE_UNAVAILABLE: 'the radio’s Wi-Fi service is unavailable',
  LIST_UNAVAILABLE: 'the radio did not return its list',
  LIST_FAILED: 'reading the list failed',
  TOO_MANY_NETWORKS: 'the radio has more than 100 saved networks',
};

export const savedReasonText = (reason: string | null | undefined): string =>
  (reason && SAVED_REASON_TEXT[reason]) || (reason ? reason.toLowerCase().replace(/_/g, ' ') : 'no reason given');

export interface DeleteWarnings {
  current: WifiSavedNetwork | null; // the network the radio is connected to now
  leavesNone: boolean; // nothing saved would remain
}

export const deleteWarnings = (selected: WifiSavedNetwork[], all: WifiSavedNetwork[]): DeleteWarnings => ({
  current: selected.find((n) => n.current) ?? null,
  leavesNone: all.length > 0 && all.every((n) => selected.some((s) => s.network_id === n.network_id)),
});

const OUTCOME_TEXT: Record<string, string> = {
  REMOVED: 'Removed',
  NOT_FOUND: 'Already gone',
  MISMATCH: 'Not removed: the radio’s entry changed',
  FAILED: 'Not removed: the radio refused',
  SKIPPED: 'Skipped',
  NO_RESULT: 'No result from the radio',
};

export const outcomeText = (outcome: string, code?: string): string =>
  `${OUTCOME_TEXT[outcome] ?? outcome}${code && outcome === 'FAILED' ? ` (${code.toLowerCase().replace(/_/g, ' ')})` : ''}`;

const OP_CODE_TEXT: Record<string, string> = {
  RADIO_NOT_CONNECTED: 'the radio was not connected, nothing was sent',
  RADIO_BUSY: 'the radio’s connection was busy, nothing was sent',
  STALE_SNAPSHOT: 'the radio’s list had changed, nothing was removed; refresh and confirm again',
  LIST_UNAVAILABLE: 'the radio could not read its list, nothing was removed',
  INTERRUPTED: 'the radio restarted during the deletion; refresh to see what remains',
};

export const operationText = (op: WifiSavedOperation, now: number): string => {
  if (op.status === 'PENDING') {
    if (op.expired) return 'No result was received from the radio.';
    const waited = ageMs(op.created_at, now) ?? 0;
    return waited > 2 * 60_000
      ? 'Waiting for the radio. If removing the current network cut its Wi-Fi connection, the result arrives when it reconnects.'
      : 'Waiting for the radio…';
  }
  const removed = op.results.filter((r) => r.outcome === 'REMOVED').length;
  const base = op.status === 'COMPLETED'
    ? `All ${removed} selected network${removed === 1 ? '' : 's'} removed.`
    : op.status === 'PARTIAL'
      ? `${removed} of ${op.requested.length} removed.`
      : 'Nothing was removed.';
  return op.code ? `${base} (${OP_CODE_TEXT[op.code] ?? op.code})` : base;
};
